import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useUrlFilters } from '../../../hooks/admin/useUrlFilters';
import { useResolveSecurityEvent, useSecurityEvents } from '../../../hooks/admin/useAdminSecurity';
import { enumField, pageField, stringField } from '../../../lib/adminFilters';
import { getApiErrorMessage } from '../../../lib/apiError';
import { SECURITY_EVENT_TYPES } from '../../../lib/monitoringTypes';
import { formatDateTime, truncate } from '../../../lib/utils';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import ExportJsonButton from '../../../components/admin/ExportJsonButton';
import DataTable from '../../../components/ui/DataTable';
import { collectEventsExport, parseSecurityHours, securityExportFilename, windowRange } from '../../../lib/adminSecurityExport';
import Pagination from '../../../components/admin/Pagination';
import InlineNotice from '../../../components/admin/InlineNotice';
import { SeverityBadge } from '../../../components/admin/StatusBadge';
import type { SecurityEvent } from '../../../types/admin';

const PAGE_SIZE = 25;

const eventsSchema = {
  type: stringField(''),
  severity: enumField(['', 'INFO', 'WARN', 'CRITICAL'], ''),
  resolved: enumField(['open', 'resolved', 'all'], 'open'),
  ip: stringField('', { maxLength: 64 }),
  userId: stringField('', { maxLength: 64 }),
  page: pageField(),
};

export default function EventsTab() {
  const { filters, setFilters, resetFilters, searchParams } = useUrlFilters(eventsSchema);
  const hours = parseSecurityHours(searchParams.get('hours'));
  const [ipInput, setIpInput] = useState(filters.ip);
  const [userInput, setUserInput] = useState(filters.userId);
  useEffect(() => setIpInput(filters.ip), [filters.ip]);
  useEffect(() => setUserInput(filters.userId), [filters.userId]);
  useEffect(() => {
    if (ipInput === filters.ip && userInput === filters.userId) return;
    const t = setTimeout(() => setFilters({ ip: ipInput.trim(), userId: userInput.trim() }), 300);
    return () => clearTimeout(t);
  }, [ipInput, userInput, filters.ip, filters.userId, setFilters]);

  const query = useSecurityEvents({
    type: filters.type || undefined,
    severity: filters.severity || undefined,
    ip: filters.ip || undefined,
    userId: filters.userId || undefined,
    resolved: filters.resolved === 'open' ? '0' : filters.resolved === 'resolved' ? '1' : undefined,
    page: filters.page,
    limit: PAGE_SIZE,
  });
  const resolveMutation = useResolveSecurityEvent();
  const [selected, setSelected] = useState<SecurityEvent | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const resolve = async (id: string) => {
    setNotice(null);
    try {
      await resolveMutation.mutateAsync({ id });
      setSelected((s) => (s && s.id === id ? { ...s, resolvedAt: new Date().toISOString() } : s));
    } catch (err) {
      setNotice(getApiErrorMessage(err, 'Failed to resolve the event'));
    }
  };

  const rows = query.data?.items ?? [];
  const hasFilters = Boolean(filters.type || filters.severity || filters.ip || filters.userId || filters.resolved !== 'open');

  return (
    <div className="space-y-4">
      <div className="glass-card grid gap-3 p-4 md:grid-cols-5">
        <select value={filters.type} onChange={(e) => setFilters({ type: e.target.value })} className="glass-input w-full px-3 py-2 text-sm">
          <option value="">All types</option>
          {SECURITY_EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filters.severity}
          onChange={(e) => setFilters({ severity: e.target.value as typeof filters.severity })}
          className="glass-input w-full px-3 py-2 text-sm"
        >
          <option value="">Any severity</option>
          <option value="INFO">INFO</option>
          <option value="WARN">WARN</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>
        <select
          value={filters.resolved}
          onChange={(e) => setFilters({ resolved: e.target.value as typeof filters.resolved })}
          className="glass-input w-full px-3 py-2 text-sm"
        >
          <option value="open">Open only</option>
          <option value="resolved">Resolved only</option>
          <option value="all">Open + resolved</option>
        </select>
        <input value={ipInput} onChange={(e) => setIpInput(e.target.value)} placeholder="IP address" className="glass-input w-full px-3 py-2 font-mono text-sm" />
        <input value={userInput} onChange={(e) => setUserInput(e.target.value)} placeholder="User ID" className="glass-input w-full px-3 py-2 font-mono text-sm" />
      </div>

      <div className="flex justify-end">
        <ExportJsonButton
          label="Download this tab"
          filename={() => securityExportFilename('events', hours)}
          collect={async () => {
            const { from, to } = windowRange(hours);
            const result = await collectEventsExport({
              type: filters.type || undefined,
              severity: filters.severity || undefined,
              ip: filters.ip || undefined,
              userId: filters.userId || undefined,
              resolved: filters.resolved === 'open' ? '0' : filters.resolved === 'resolved' ? '1' : undefined,
              from,
              to,
            });
            return {
              exportedAt: new Date().toISOString(),
              hours,
              from,
              to,
              filters,
              truncated: result.truncated,
              total: result.total,
              items: result.items,
            };
          }}
        />
      </div>

      {notice && (
        <InlineNotice tone="error" onDismiss={() => setNotice(null)}>
          {notice}
        </InlineNotice>
      )}
      {query.isError && <InlineNotice tone="error">{getApiErrorMessage(query.error, 'Failed to load security events')}</InlineNotice>}

      <div className={`glass-card overflow-hidden ${query.isFetching && !query.isPending ? 'opacity-80' : ''}`}>
        {hasFilters && (
          <div className="flex justify-end border-b border-glass px-4 py-2">
            <button type="button" className="text-xs text-accent2 underline-offset-2 hover:underline" onClick={() => resetFilters()}>
              Clear filters
            </button>
          </div>
        )}
        <DataTable<SecurityEvent>
          data={rows}
          isLoading={query.isPending}
          emptyMessage={filters.resolved === 'open' ? 'No open events.' : 'No events match these filters.'}
          onRowClick={(row) => setSelected(row)}
          columns={[
            { header: 'Time', accessor: (e) => <span className="text-glass-text-2">{formatDateTime(e.createdAt)}</span> },
            { header: 'Severity', accessor: (e) => <SeverityBadge severity={e.severity} /> },
            { header: 'Type', accessor: (e) => <span className="font-mono text-xs">{e.type}</span> },
            {
              header: 'Message',
              accessor: (e) => (
                <span className="block max-w-[360px] truncate" title={e.message}>
                  {truncate(e.message, 100)}
                </span>
              ),
            },
            { header: 'IP', accessor: (e) => <span className="font-mono text-xs text-glass-text-2">{e.ip || '—'}</span> },
            {
              header: 'Route',
              accessor: (e) => (
                <span className="block max-w-[220px] truncate font-mono text-xs text-glass-text-2" title={e.route ?? undefined}>
                  {e.route || '—'}
                </span>
              ),
            },
            {
              header: '',
              accessor: (e) =>
                e.resolvedAt ? (
                  <span className="text-xs text-glass-text-3" title={formatDateTime(e.resolvedAt)}>
                    resolved
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      void resolve(e.id);
                    }}
                    disabled={resolveMutation.isPending}
                    className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    Resolve
                  </button>
                ),
            },
          ]}
        />
        <Pagination pagination={query.data?.pagination} onPageChange={(page) => setFilters({ page })} label="events" isFetching={query.isFetching && !query.isPending} />
      </div>

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.type} · ${selected.severity}` : 'Event'}
        size="lg"
        footer={
          <div className="flex justify-between gap-3">
            {selected && !selected.resolvedAt ? (
              <Button variant="default" size="sm" onClick={() => void resolve(selected.id)} disabled={resolveMutation.isPending}>
                <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden />
                Mark resolved
              </Button>
            ) : (
              <span />
            )}
            <Button variant="outline" onClick={() => setSelected(null)}>
              Close
            </Button>
          </div>
        }
      >
        {selected && (
          <div className="space-y-4 text-sm">
            <p className="text-glass-text">{selected.message}</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:grid-cols-3">
              <div>
                <dt className="uppercase tracking-wide text-glass-text-3">When</dt>
                <dd className="text-glass-text">{formatDateTime(selected.createdAt)}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wide text-glass-text-3">IP</dt>
                <dd className="font-mono text-glass-text">{selected.ip || '—'}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wide text-glass-text-3">Route</dt>
                <dd className="break-all font-mono text-glass-text">{selected.route || '—'}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wide text-glass-text-3">User</dt>
                <dd className="break-all font-mono text-glass-text">{selected.userId || '—'}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wide text-glass-text-3">Organisation</dt>
                <dd className="break-all font-mono text-glass-text">{selected.organizationId || '—'}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wide text-glass-text-3">Resolved</dt>
                <dd className="text-glass-text">{selected.resolvedAt ? formatDateTime(selected.resolvedAt) : 'open'}</dd>
              </div>
            </dl>
            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-glass-text-3">Details</div>
              <pre className="max-h-80 overflow-auto rounded-lg border border-glass bg-glass-1 p-3 font-mono text-xs text-glass-text glass-scroll">
                {selected.details ? JSON.stringify(selected.details, null, 2) : '{}'}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
